import { isQaFailureRunStatus, isPreviewEligibleRunStatus } from "@enrein/shared-contracts";
import type { Step5RoomTruthV1, StudioApiClient } from "../../api.js";
import {
  getPreviewOpenBlockReason,
  PREVIEW_UNAVAILABLE_MESSAGE,
  withPreviewBaseHref,
} from "../studio-shared/preview-open-guard.js";

export function workbenchPreviewNotice(input: {
  readonly previewUrl?: string | null;
  readonly workingVersionLabel?: string | null;
  readonly latestRunStatus?: string | null;
}): { readonly kind: "available" | "blocked" | "waiting"; readonly message: string } {
  if (input.previewUrl?.trim()) {
    const version = input.workingVersionLabel?.trim() || "current working version";
    return {
      kind: "available",
      message: `A generated preview is available for the current working version ${version}.`,
    };
  }
  if (isPreviewEligibleRunStatus(input.latestRunStatus ?? undefined)) {
    return {
      kind: "blocked",
      message:
        "Quality gate passed for this build, but the preview document is not ready to open yet. Retry Open preview or rebuild if this persists.",
    };
  }
  if (isQaFailureRunStatus(input.latestRunStatus)) {
    return {
      kind: "blocked",
      message: "Generation did not pass quality gate. Review facts and rebuild before opening preview.",
    };
  }
  if (input.latestRunStatus && ["QUEUED", "RUNNING", "PENDING", "QA_RUNNING", "QA_QUEUED"].includes(input.latestRunStatus)) {
    return {
      kind: "waiting",
      message: "Preview will appear after the current build finishes quality gate.",
    };
  }
  return {
    kind: "waiting",
    message: "No preview exists yet. Confirm the candidate and start a build to generate one.",
  };
}

export type GreenfieldPreviewLoadResult =
  | { readonly ok: true; readonly previewHtml: string; readonly runId: string }
  | { readonly ok: false; readonly error: string };

/**
 * Load preview HTML for in-Studio rendering (iframe srcDoc).
 * Does not call window.open — Cursor/Electron new tabs often replace about:blank
 * with a Recents shell and never show the written document (F-04b).
 */
export async function loadGreenfieldProjectPreview(input: {
  readonly api: Pick<StudioApiClient, "getProjectPreview">;
  readonly projectId: string;
  readonly truth: Pick<Step5RoomTruthV1, "previewUrl" | "latestRun" | "versions"> | null;
  readonly studioOrigin?: string | null;
}): Promise<GreenfieldPreviewLoadResult> {
  const latestStatus = input.truth?.latestRun?.status ?? null;
  const previewUrl = input.truth?.previewUrl ?? null;
  const guardReason = getPreviewOpenBlockReason({
    previewHtml: null,
    previewUrl,
    previewVersionId: input.truth?.versions.clientVisible ?? input.truth?.versions.working ?? null,
    qaFailure: isQaFailureRunStatus(latestStatus),
    latestRunStatus: latestStatus,
  });
  if (guardReason && !isPreviewEligibleRunStatus(latestStatus ?? undefined)) {
    return { ok: false, error: guardReason };
  }
  if (isQaFailureRunStatus(latestStatus)) {
    return { ok: false, error: guardReason ?? PREVIEW_UNAVAILABLE_MESSAGE };
  }

  try {
    const preview = await input.api.getProjectPreview(input.projectId);
    const html = preview.previewHtml;
    const openGuard = getPreviewOpenBlockReason({
      previewHtml: html,
      previewUrl: previewUrl ?? `/preview/${preview.runId}`,
      previewVersionId: preview.runId,
      qaFailure: false,
      latestRunStatus: latestStatus ?? "QA_PASSED",
    });
    if (openGuard) {
      return { ok: false, error: openGuard };
    }
    const origin =
      input.studioOrigin ??
      (typeof window !== "undefined" ? window.location.origin : null);
    return {
      ok: true,
      runId: preview.runId,
      previewHtml: withPreviewBaseHref(html, origin),
    };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : PREVIEW_UNAVAILABLE_MESSAGE,
    };
  }
}

/** @deprecated Use loadGreenfieldProjectPreview — kept name alias for older call sites during rename. */
export const openGreenfieldProjectPreview = loadGreenfieldProjectPreview;
